import { useState } from "react";
import { useLayoutContext } from "@/components/Layout";
import { useBillingCurrent, useBillingHistory } from "@/api/hooks/use-billing";
import { KpiCard } from "@/components/KpiCard";
import { KpiGrid } from "@/components/KpiGrid";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { StatusBadge } from "@/components/StatusBadge";
import { PaymentMethodCard } from "@/components/PaymentMethodCard";
import { formatUsd } from "@/lib/format";

export function Billing() {
  const { tenantId } = useLayoutContext();
  const currentQ = useBillingCurrent(tenantId);
  const historyQ = useBillingHistory(tenantId);
  const [showMethodology, setShowMethodology] = useState(false);

  if (!tenantId) {
    return <EmptyState message="Enter a Tenant ID in the sidebar to get started" />;
  }

  if (currentQ.isLoading) {
    return <LoadingSkeleton cards={4} rows={5} />;
  }

  if (currentQ.isError) {
    return (
      <ErrorState
        message={currentQ.error?.message || "Failed to load billing data"}
        onRetry={() => { currentQ.refetch(); historyQ.refetch(); }}
      />
    );
  }

  const current = currentQ.data!;
  const history = historyQ.data ?? [];
  const invoice = current.projected_invoice || current.total_invoice_amount || "0";

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold text-fg">Billing</h1>

      <PaymentMethodCard />

      {/* Current Period Card */}
      <div className="rounded-lg border border-line-subtle bg-surface p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-fg">Current Period</h2>
          <StatusBadge status={current.status} />
        </div>

        <p className="text-xs text-fg-muted">
          {current.period_start} &mdash; {current.period_end}
        </p>

        <KpiGrid>
          <KpiCard
            label="Verified Savings"
            value={formatUsd(current.total_verified_savings)}
            color="green"
          />
          <KpiCard
            label="Projected Invoice"
            value={formatUsd(invoice)}
          />
          <KpiCard
            label="Billable Requests"
            value={String(current.billable_requests)}
          />
        </KpiGrid>

        {/* Invoice Calculation */}
        <div className="rounded border border-line-subtle bg-canvas px-4 py-3 space-y-1">
          <p className="text-xs font-medium text-fg-muted uppercase tracking-wider mb-2">
            Invoice Calculation
          </p>
          <div className="flex justify-between text-xs">
            <span className="text-fg-muted">
              Savings Fee ({parseFloat(current.savings_fee_rate || "0.25") * 100}% of verified savings)
            </span>
            <span className="text-fg font-mono">{formatUsd(current.savings_fee || "0")}</span>
          </div>
          <div className="border-t border-line-subtle mt-2 pt-2 flex justify-between text-sm font-medium">
            <span className="text-fg">Total</span>
            <span className="text-fg font-mono">{formatUsd(invoice)}</span>
          </div>
        </div>
      </div>

      {/* Methodology */}
      <div className="rounded-lg border border-line-subtle bg-surface">
        <button
          type="button"
          onClick={() => setShowMethodology(!showMethodology)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm text-fg-muted hover:text-fg transition-colors"
        >
          <span>How we calculate savings</span>
          <span className="text-xs">{showMethodology ? "Hide" : "Show"}</span>
        </button>
        {showMethodology && (
          <div className="px-4 pb-4 text-xs text-fg-muted leading-relaxed space-y-2">
            <p>
              We calculate savings by comparing what you would have paid at your requested model
              and original token count vs what was actually billed to the provider.
            </p>
            <p>
              We charge{" "}
              <strong className="text-fg">
                {Number((parseFloat(current.savings_fee_rate || "0.2") * 100).toFixed(4))}% of verified savings
              </strong>
              . No platform fees.
            </p>
            <p>
              Every savings calculation is auditable to the individual request level. Each monthly
              statement, under Statements, has a per-request export for independent verification.
            </p>
          </div>
        )}
      </div>

      {/* Invoice History */}
      <div className="rounded-lg border border-line-subtle bg-surface">
        <div className="px-4 py-3 border-b border-line-subtle">
          <h2 className="text-sm font-medium text-fg">Invoice History</h2>
        </div>
        {history.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-fg-muted">
            No billing history yet
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-line-subtle text-fg-muted">
                <th className="px-4 py-2 text-left font-medium">Period</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Baseline</th>
                <th className="px-4 py-2 text-right font-medium">Actual</th>
                <th className="px-4 py-2 text-right font-medium">Savings</th>
                <th className="px-4 py-2 text-right font-medium">Invoice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {history.map((p) => (
                <tr key={p.id} className="hover:bg-surface-2">
                  <td className="px-4 py-2 text-fg font-mono">
                    {p.period_start}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="px-4 py-2 text-right text-fg-muted font-mono">
                    {formatUsd(p.total_baseline_cost)}
                  </td>
                  <td className="px-4 py-2 text-right text-fg font-mono">
                    {formatUsd(p.total_actual_cost)}
                  </td>
                  <td className="px-4 py-2 text-right text-safe-green font-mono">
                    {formatUsd(p.total_verified_savings)}
                  </td>
                  <td className="px-4 py-2 text-right text-fg font-mono font-medium">
                    {formatUsd(p.total_invoice_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
